import "firebase/firestore";
import * as mediasoupClient from "mediasoup-client";
import Logger from "./Logger";
import * as e2e from "./e2e";
import store from "../store";
import { detect } from "detect-browser";
import { SnackbarProgrammatic as Snackbar } from "buefy";
import { ToastProgrammatic as Toast } from "buefy";
import messageHandler from "./messageHandler";

// const VIDEO_CONSTRAINTS = {
//   qvga: { width: { ideal: 320 }, height: { ideal: 240 } },
//   vga: { width: { ideal: 640 }, height: { ideal: 480 } },
//   hd: { width: { ideal: 1280 }, height: { ideal: 720 } },
// };

const msg = `IPs:
  firestore.googleapis.com -> floating ips
  socket.qonda.cloud -> floating ips
  mediasoup.qonda.cloud -> 18.159.85.247
  v2.goqonda.io -> 151.101.1.195 | 151.101.65.195
  app.goqonda.io -> 151.101.1.195 | 151.101.65.195
  Für Konferenz Admins: cloudfunctions.net

  Destination ports - UDP/TCP Ports:
  80; 443; 4443; 5000;40000-49999`;
const msgHtml = `<p>Could not connect to Mediasoup server. Either the server is unreachable 
  or your firewall blocks the connection. Contact your IT administrator to allow the following 
  IPs and Ports:</p><br>
  <p>firestore.googleapis.com -> floating ips</p>
  <p>socket.qonda.cloud -> floating ips</p>
  <p>mediasoup.qonda.cloud -> 18.159.85.247</p>
  <p>v2.goqonda.io -> 151.101.1.195 | 151.101.65.195</p>
  <p>app.goqonda.io -> 151.101.1.195 | 151.101.65.195</p>
  <p>Für Konferenz Admins: cloudfunctions.net</p><br>
  <p>Destination ports - UDP/TCP Ports:</p>
  <p>80; 443; 4443; 5000;40000-49999</p>`;

let VIDEO_CONSTRAINTS = {
  video: {
    width: { min: 320, ideal: 1080, max: 1920 },
    height: { min: 240, ideal: 720, max: 1080 },
    aspectRatio: { ideal: 1.7777777778 },
  },
};

const AUDIO_CONSTRAINTS = {
  echoCancellation: false,
  noiseSuppression: false,
  autoGainControl: false,
};

const PC_PROPRIETARY_CONSTRAINTS = {
  optional: [{ googDscp: true }],
};

// Used for simulcast webcam video.
const WEBCAM_SIMULCAST_ENCODINGS = [
  { scaleResolutionDownBy: 4, maxBitrate: 500000 },
  { scaleResolutionDownBy: 2, maxBitrate: 1000000 },
  { scaleResolutionDownBy: 1, maxBitrate: 5000000 },
];

// Used for VP9 webcam video.
const WEBCAM_KSVC_ENCODINGS = [{ scalabilityMode: "S3T3_KEY" }];

// Used for simulcast screen sharing.
const SCREEN_SHARING_SIMULCAST_ENCODINGS = [
  { dtx: true, maxBitrate: 1500000, ssrc: 24 },
  { dtx: true, maxBitrate: 6000000, rid: "why" },
];

// Used for VP9 screen sharing.
const SCREEN_SHARING_SVC_ENCODINGS = [{ scalabilityMode: "S3T3", dtx: true }];

const EXTERNAL_VIDEO_SRC = "/resources/videos/video-audio-stereo.mp4";

const logger = new Logger("RoomClient.js");

// logger.info('IM DA STORAGE',store.state.mediasoup)

export default class RoomClient {
  /**
   * @param  {Object} data
   * @param  {Object} data.store - The Redux store.
   */

  constructor({
    mediasoupConfig,
    roomId,
    peerId,
    role,
    isFloor,
    displayName,
    device,
    handlerName,
    useSimulcast,
    useSharingSimulcast,
    forceTcp,
    produce,
    consume,
    forceH264,
    forceVP9,
    svc,
    externalVideo,
    e2eKey,
    produceLang,
    consumeLang,
    clientTranslator,
    eventId,
    isModerator,
    connectionType,
    allowedToSpeak,
    testViewer,
  }) {
    logger.info(
      `Creating roomClient with roomId: ${roomId}, peerId: ${peerId} displayName: ${displayName} device: ${device.flag}`,
    );

    this._mediasoupConfig = mediasoupConfig;
    this._roomId = roomId;
    // Closed flag.
    // @type {Boolean}
    this._closed = false;
    this._role = role;
    this._spatialLayers = 2;
    this._eventId = eventId;
    this._isModerator = isModerator;

    // Display name.
    // @type {String}
    this._displayName = displayName;

    this._timeout = 10000;

    // Device info.
    // @type {Object}
    this._device = device;
    this._routerId = null;

    // Whether we want to force RTC over TCP.
    // @type {Boolean}
    this._forceTcp = forceTcp;

    // Whether we want to produce audio/video.
    // @type {Boolean}
    this._produce = produce;
    this._isFloor = isFloor;

    // Whether we should consume.
    // @type {Boolean}
    this._consume = consume;

    this._clientTranslator = clientTranslator;

    // Force H264 codec for sending.
    this._forceH264 = Boolean(forceH264);

    // Force VP9 codec for sending.
    this._forceVP9 = Boolean(forceVP9);

    // External video.
    // @type {HTMLVideoElement}
    this._externalVideo = null;

    // Enabled end-to-end encryption.
    this._e2eKey = e2eKey;

    // MediaStream of the external video.
    // @type {MediaStream}
    this._externalVideoStream = null;

    // User's connection strength.
    // @type {string}
    this._connectionType = connectionType;

    // Used to check data channel msg
    this._testMsgReceived = null;

    // logger.info('externalvideo', externalVideo)
    if (externalVideo) {
      this._externalVideo = document.createElement("video");

      this._externalVideo.controls = true;
      this._externalVideo.muted = true;
      this._externalVideo.loop = true;
      this._externalVideo.setAttribute("playsinline", "");
      this._externalVideo.src = EXTERNAL_VIDEO_SRC;

      this._externalVideo.play().catch((error) => logger.warn("externalVideo.play() failed:%o", error));
    }

    // Custom mediasoup-client handler name (to override default browser
    // detection if desired).
    // @type {String}
    this._handlerName = handlerName;

    // Whether simulcast should be used.
    // @type {Boolean}
    this._useSimulcast = useSimulcast;

    // Whether simulcast should be used in desktop sharing.
    // @type {Boolean}
    this._useSharingSimulcast = useSharingSimulcast;

    // Protoo URL.
    // @type {String}

    // Test viewer status
    this._testViewer = testViewer;

    // protoo-client Peer instance.
    // @type {protooClient.Peer}
    this._fbPeer = null;
    this._peerId = peerId;
    // to store the joined peers
    this._peers = new Map();

    // mediasoup-client Device instance.
    // @type {mediasoupClient.Device}
    this._mediasoupDevice = null;

    // mediasoup Transport for sending.
    // @type {mediasoupClient.Transport}
    this._sendTransport = null;

    // mediasoup Transport for receiving.
    // @type {mediasoupClient.Transport}
    this._recvTransport = null;
    // Local mic mediasoup Producer.
    // @type {mediasoupClient.Producer}
    this._micProducer = null;

    // Local webcam mediasoup Producer.
    // @type {mediasoupClient.Producer}
    this._webcamProducer = null;

    // Local share mediasoup Producer.
    // @type {mediasoupClient.Producer}
    this._shareProducer = null;

    // Local share mediasoup audio Producer.
    // @type {mediasoupClient.Producer}
    this._shareAudioProducer = null;

    // Local chat DataProducer.
    // @type {mediasoupClient.DataProducer}
    this._dataProducer = null;

    // Local chat DataProducer.
    // @type {mediasoupClient.DataProducer}
    this._chatDataProducer = null;

    // Local bot DataProducer.
    // @type {mediasoupClient.DataProducer}
    this._botDataProducer = null;

    // mediasoup Consumers.
    // @type {Map<String, mediasoupClient.Consumer>}
    this._consumers = new Map();

    // mediasoup DataConsumers.
    // @type {Map<String, mediasoupClient.DataConsumer>}
    this._dataConsumers = new Map();

    // Map of webcam MediaDeviceInfos indexed by deviceId.
    // @type {Map<String, MediaDeviceInfos>}
    this._webcams = new Map();

    // Used to Wait for Producer Id
    this._producerIdTemp = null;

    // Used to Wait for joined users list
    this._gotJoinedUsers = false;

    // Temporary storage for joined users
    this._joinedUsersTemp = [];

    // UserLanguage
    this._produceLang = produceLang?.toUpperCase();
    this._consumeLang = consumeLang;
    this.micPaused = false;
    this.vidPaused = false;

    // Speaking status
    this._allowedToSpeak = allowedToSpeak;

    // Local Webcam.
    // @type {Object} with:
    // - {MediaDeviceInfo} [device]
    // - {String} [resolution] - 'qvga' / 'vga' / 'hd'.
    // https://travio--websocket-jrglmfpb.web.app/
    this._webcam = {
      device: null,
      resolution: "hd",
    };

    // Set custom SVC scalability mode.
    if (svc) {
      WEBCAM_KSVC_ENCODINGS[0].scalabilityMode = `${svc}_KEY`;
      SCREEN_SHARING_SVC_ENCODINGS[0].scalabilityMode = svc;
    }

    if (this._e2eKey && e2e.isSupported()) {
      e2e.setCryptoKey("setCryptoKey", this._e2eKey, true);
    }
  }
  _connect() {
    const websocket = (this.websocket = new WebSocket(this._mediasoupConfig.ServiceEndpointWebsocket));

    websocket.onclose = ({ wasClean, code, reason }) => {
      if ((code === 1006 || code === 1005) && wasClean === false) {
        Snackbar.open({
          message: msgHtml,
          position: "is-top",
          actionText: "Copy & Close",
          indefinite: true,
          onAction: () => {
            navigator.clipboard.writeText(msg);
          },
        });
      }
      logger.info(`WebSocket onClose event:   ${JSON.stringify({ wasClean, code, reason })}`);
      this._connect();
    };

    websocket.onerror = (event) => {
      console.error("WebSocket error event", event);
    };

    websocket.onmessage = ({ data }) => {
      try {
        const msg = JSON.parse(data);
        logger.info("tmp", msg);
        if (msg.action !== "redirect") {
          let msgData = JSON.parse(msg.data);
          if (msgData.roomId === this._roomId) {
            messageHandler(this, msgData);
          }
        }
      } catch (e) {
        console.error(e);
      }
    };

    websocket.onopen = () => {
      // logger.info('Websocket on Open');
      store.commit("mediasoup/set_websocketReady", true);
      const payload = {
        command: "hello",
        roomId: this._roomId,
        peerId: "",
        senderId: this._peerId,
      };

      websocket.send(
        // This message will be routed to the '$default' route as 'routeB'
        // has not been defined
        JSON.stringify({
          action: "init_message",
          data: JSON.stringify(payload),
        }),
      );
    };
  }
  /**
   * Used to send websocket msgs to the server
   */
  sendMessage(command, data) {
    let payload = {
      command: command,
      peerId: "server",
      roomId: this._roomId,
      senderId: this._peerId,
      ...data,
    };
    logger.info("🚀 Sending Message 🚀", payload);
    this.websocket.send(
      // This message will be routed to the '$default' route as 'routeB'
      // has not been defined
      JSON.stringify({ action: "peer_message", data: JSON.stringify(payload) }),
    );
  }
  /**
   * Used to send websocket msgs to the server and all peers
   */
  sendAllMessage(command, data) {
    let payload = {
      command: command,
      peerId: "all",
      roomId: this._roomId,
      senderId: this._peerId,
      ...data,
    };
    logger.info("sendAllMessage", payload);
    this.websocket.send(
      // This message will be routed to the '$default' route as 'routeB'
      // has not been defined
      JSON.stringify({ action: "room_message", data: JSON.stringify(payload) }),
    );
  }
  /**
   * Used to send websocket msgs to a specific peer
   */
  sendPeerMessage(command, peerId, data) {
    let payload = {
      command: command,
      peerId: peerId,
      roomId: this._roomId,
      senderId: this._peerId,
      ...data,
    };
    logger.info("sendPeerMessage", payload);
    this.websocket.send(
      // This message will be routed to the '$default' route as 'routeB'
      // has not been defined
      JSON.stringify({ action: "peer_message", data: JSON.stringify(payload) }),
    );
  }
  sendKeepAlive() {
    const payload = {
      command: "peer_alive",
      roomId: this._roomId,
      peerId: "all",
      senderId: this._peerId,
      lastAlive: Date.now(),
    };
    logger.info("🚀 Sending Keep-Alive 🚀", payload);
    try {
      this.websocket.send(JSON.stringify({ action: "keep_alive", data: JSON.stringify(payload) }));
    } catch (err) {
      logger.error("Error sending keep alive", err);
    }
  }
  /**
   * Sends keep_alive at random intervals
   */
  sendKeepAliveWithRandInterval(startInMs, endinMs) {
    let i;
    let randInterval = this.generateRandomNum(5000, 20000);
    logger.info("initial rand val: ", randInterval);
    let randomize = () => {
      this.sendKeepAlive();

      randInterval = this.generateRandomNum(startInMs, endinMs);
      clearInterval(i);
      i = setInterval(randomize, randInterval);
    };
    i = setInterval(randomize, randInterval);
  }

  async close() {
    if (this._closed) return;

    this._closed = true;

    logger.info("close()");

    // Close Peer

    // Close mediasoup Transports.
    if (this._sendTransport) {
      this._sendTransport.close();
    }

    if (this._recvTransport) {
      this._recvTransport.close(); //should be closing producers as well
    }
    const payload = {
      closedPeerId: this._peerId,
      displayName: this._displayName,
    };
    await this.sendAllMessage("user_close", payload);
    this.closeWebsocket();
  }
  closeWebsocket() {
    this.websocket.close();
  }

  // messageHandler(data) {
  //   if (data.peerId !== this._peerId && data.peerId !== "all") {
  //     // // logger.info('message not for me', data)
  //     return;
  //   }
  //   switch (data.command) {
  //     case "server_started":
  //       //this.reconnect(data)
  //       this.close();
  //       break;
  //     case "router":
  //       this.getSctpAndSendBackToServer(data);
  //       break;
  //     case "transport_created":
  //       this.initTransportsAndSendDtls(data); //start clientside transports and sends
  //       break;
  //     case "connected_transport":
  //       // logger.info('connectedTransportdata', data)
  //       break;
  //     case "producer_id":
  //       this.storeProducer(data);
  //       break;
  //     case "new_consumer":
  //       this.initConsumer(data);
  //       break;
  //     case "new_data_consumer":
  //       this.createDataConsumer(data);
  //       break;
  //     case "unpause_client_consumer":
  //       logger.info("unpause_client_consumer", data);
  //       this.startConsumer(data);
  //       break;
  //     case "new_user":
  //       if (data.peer.id !== this._peerId) {
  //         store.commit("mediasoup/add_peer", data.peer);
  //         store.commit("mediasoup/add_to_all_peer", data.peer);
  //       }
  //       break;
  //     // case "joined_users":
  //     //   this._joinedUsersTemp = [...this._joinedUsersTemp, ...data.peers]

  //     //   if (data.totalSize === this._joinedUsersTemp.length){
  //     //     this.storeJoinedPeers(this._joinedUsersTemp);
  //     //   }
  //     //   // this.storeAllJoinedPeers(data)
  //     //   break;
  //     // case 'all_joined_users':
  //     // 	this.storeAllJoinedPeers(data)
  //     // 	break;
  //     case "max_participants":
  //       store.commit("mediasoup/set_maxParticipantsNotification", true);
  //       break;
  //     case "set_hand_raised":
  //       store.commit("mediasoup/set_handRaised", { id: data.id, handRaised: data.handRaised });
  //       break;
  //     case "peer_mute":
  //       // logger.info('peer_mute data', data)

  //       if (data.peerId !== this._peerId) {
  //         const payload = { id: data.peerToMute, micPaused: data.micPaused };
  //         store.commit("mediasoup/set_peer_micPaused", payload);
  //       }
  //       break;
  //     case "peer_video_mute":
  //       // logger.info('peer_video_mute data', data)
  //       if (data.peerId !== this._peerId) {
  //         const payload = { id: data.peerToMute, vidPaused: data.vidPaused };
  //         store.commit("mediasoup/set_peer_vidPaused", payload);
  //       }
  //       break;
  //     case "remote_mute":
  //       this.muteMic();
  //       break;
  //     case "remote_video_mute":
  //       this.videoMute();
  //       break;
  //     case "user_close":
  //       // logger.info('closing_userr', data)
  //       store.commit("mediasoup/remove_peer", data.closedPeerId);
  //       store.commit("mediasoup/remove_from_allPeer", data.closedPeerId);
  //       this.setConferenceState(data.closedPeerId);
  //       break;
  //     case "peer_onAir":
  //       if (data.peerId !== this._peerId) {
  //         const payload = { id: data.onAirId, onAir: data.onAir };
  //         store.commit("mediasoup/set_peer_onAir", payload);
  //       }
  //       break;
  //     case "presentation_started":
  //       store.commit("mediasoup/start_presentation", data.presenterId);
  //       break;
  //     case "presentation_stopped":
  //       store.commit("mediasoup/stop_presentation");
  //       store.commit("mediasoup/remove_screen_consumer", data.presenterId);
  //       store.commit("mediasoup/remove_screen_audio_consumer", data.presenterId);
  //       logger.info("presentation stopped, presenterId", data.presenterId);
  //       if (data.presenterId === this._peerId) {
  //         this.disableShare();
  //         store.commit("mediasoup/set_sharing", false);
  //       }
  //       break;
  //     case "current_conference_state":
  //       if (data.isPresentation) {
  //         store.commit("mediasoup/start_presentation", data.presenterId);
  //       }
  //       if (data.isSpotlight) {
  //         logger.info("Spotlight running, Room.Vue, line: 509");
  //         store.commit("mediasoup/set_spotlight");
  //         store.commit("mediasoup/set_spotlight_peerId", data.spotlightPeerId);
  //       }
  //       break;

  //     case "set_spotlight":
  //       // Sets new spotlight
  //       if (!store.getters["mediasoup/isSpotlight"] && data.spotlightPeerId !== null) {
  //         store.commit("mediasoup/set_spotlight_peerId", data.spotlightPeerId);
  //         store.commit("mediasoup/set_spotlight");
  //       }
  //       // switches spotlight
  //       else if (
  //         store.getters["mediasoup/getSpotlightPeerId"] !== data.spotlightPeerId &&
  //         data.spotlightPeerId !== null
  //       ) {
  //         store.commit("mediasoup/set_spotlight_peerId", data.spotlightPeerId);
  //       }
  //       // Removes spotlight
  //       else {
  //         store.commit("mediasoup/set_spotlight_peerId", null);
  //         store.commit("mediasoup/remove_spotlight");
  //       }
  //       break;

  //     case "changed_produceLang":
  //       if (data.peerId !== this._peerId) {
  //         const payload = { id: data.peerChanged, produceLang: data.lang };
  //         store.commit("mediasoup/set_clientTranslatorOut", { id: data.peerChanged, currentOutputLang: data.lang });
  //         store.commit("mediasoup/set_peer_produce_lang", payload);
  //       }
  //       break;
  //     case "changed_consumeLang":
  //       if (data.peerId !== this._peerId) {
  //         const payload = { id: data.peerChanged, consumeLang: data.lang };
  //         store.commit("mediasoup/set_peer_consume_lang", payload);
  //       }
  //       break;
  //     case "remote_offAir":
  //       logger.info("remoteoffair", data.senderId);
  //       store.commit("mediasoup/set_onAir", false);
  //       if (data.forced) {
  //         store.commit(
  //           "mediasoup/set_forceTakeover",
  //           `User: "${data.displayName}" forced took over the interpretation!`,
  //         );
  //       }
  //       this.setOnAir(false);
  //       break;
  //     case "remote_close":
  //       // logger.info('remote_close', data)
  //       // logger.info('remote_close', data)
  //       if (data.peerId === this._peerId) {
  //         this.close();
  //         alert("You got Kicked");
  //         window.location.href = "https://goqonda.io";
  //       }
  //       break;
  //     case "active_handover_request":
  //       logger.info("active_handover_request");
  //       this.setHandoverData(data);
  //       break;
  //     case "active_request_accepted":
  //       store.commit("mediasoup/set_activeRequestAccepted", true);
  //       store.commit("mediasoup/set_activeRequestAnswererId", data.answererId);
  //       store.commit("mediasoup/set_activeRequestAnswererName", data.answererName);
  //       break;
  //     case "active_request_rejected":
  //       logger.info("active_request_rejected rcvd");
  //       store.commit("mediasoup/set_activeRequestRejected", true);
  //       store.commit("mediasoup/set_activeRequestAnswererId", data.answererId);
  //       store.commit("mediasoup/set_activeRequestAnswererName", data.answererName);
  //       break;
  //     case "passive_handover_request":
  //       this.setHandoverData(data);
  //       break;
  //     case "passive_request_accepted":
  //       store.commit("mediasoup/set_passiveRequestAccepted", true);
  //       store.commit("mediasoup/set_passiveRequestPending", false);
  //       break;
  //     case "passive_request_rejected":
  //       store.commit("mediasoup/set_passiveRequestRejected", true);
  //       store.commit("mediasoup/set_passiveRequestPending", false);
  //       store.commit("mediasoup/set_translatorReady", false);
  //       break;
  //     case "allowed_switchToHost":
  //       // logger.info('allowed_switchToHost')
  //       store.commit("mediasoup/set_allowedToSpeak", { viewerId: data.viewerId, allowedToSpeak: true });
  //       break;
  //     case "switched_toHost":
  //       // logger.info('switched_toHost', data)
  //       store.commit("mediasoup/set_peer_role", { id: data.newHostId, role: "host" });
  //       break;
  //     case "switch_to_viewer":
  //       // logger.info('switched_toViewer', data)
  //       if (data.newViewerId === this._peerId) {
  //         this.switchToViewer(data);
  //       } else {
  //         store.commit("mediasoup/set_peer_role", { id: data.newViewerId, role: "viewer" });
  //         store.commit("mediasoup/set_allowedToSpeak", { viewerId: data.newViewerId, allowedToSpeak: false });
  //         store.commit("mediasoup/set_reqToSpeakOngoing", { requesterId: data.newViewerId, reqToSpeakOngoing: false });
  //       }
  //       break;
  //     case "request_to_speak_ongoing":
  //       store.commit("mediasoup/set_reqToSpeakOngoing", { requesterId: data.requesterId, reqToSpeakOngoing: true });
  //       break;
  //     case "activeSpeaker":
  //       // eslint-disable-next-line
  //       const payload = { id: data.activeSpeakerId, volume: data.volume };
  //       store.commit("mediasoup/set_activeSpeaker", payload);
  //       break;
  //     case "change_resolution":
  //       this.changeWebcamResolution(data.resolution);
  //       break;
  //     case "toggle_resolution":
  //       this.toggleMaxlayers();
  //       break;
  //     case "toggle_layers":
  //       break;
  //     case "set_preferedConsumerLayer":
  //       this.setConsumerPreferredLayers(data.consumerId, data.spatialLayer, data.temporalLayer);
  //       break;

  //     // case "set_maxSendingLayer":
  //     //   this.setMaxSendingSpatialLayer(data.layer)
  //     //   break;

  //     case "connection_strength":
  //       // logger.info('connection_strength', data)
  //       logger.info("setting connectionstrenght");
  //       logger.info("peers before", store.getters["mediasoup/getPeers"]);
  //       store.commit("mediasoup/set_peer_connectionType", data);
  //       logger.info("peers after", store.getters["mediasoup/getPeers"]);
  //       break;
  //     case "set_lastAlive":
  //       store.commit("mediasoup/set_lastAlive", { id: data.id, lastAlive: data.lastAlive });
  //   }
  // }

  // eslint-disable-next-line
  async reconnect(data) {
    try {
      this._recvTransport.close();
      this._recvTransport = null;

      this._sendTransport.close();
      this._sendTransport = null;

      this._micProducer.close();
      this._micProducer = null;

      this._webcamProducer.close();
      this._webcamProducer = null;

      this._shareProducer.close();
      this._shareProducer = null;

      for (let consumer of this._consumers) {
        consumer.close();
        this._consumers.delete(consumer.id);
      }
    } catch (err) {
      logger.error(err);
    }

    store.commit("mediasoup/reset");

    this._connect();
  }

  async getSctpAndSendBackToServer(data) {
    // data should have routerId
    // logger.info('getSctpAndSendBackToServer',data)

    this._mediasoupDevice = new mediasoupClient.Device({
      handlerName: this._handlerName,
    });

    logger.info("device handler", this._mediasoupDevice.handlerName);

    const router = data.router;
    logger.info("data.router", data.router);
    this._routerId = router.id;

    const routerRtpCapabilities = router.rtpCapabilities;

    await this._mediasoupDevice.load({ routerRtpCapabilities });

    // set Capabilites so Server can start with initiating the Transports

    const payload = {
      device: this._device,
      rtpCapabilities: this._mediasoupDevice.rtpCapabilities,
      sctpCapabilities: this._mediasoupDevice.sctpCapabilities,
    };
    // logger.info('payload in sctp', payload)
    this.sendMessage("user_capabilities", payload);
  }
  async initTransportsAndSendDtls(data) {
    // logger.info('initTransportsAndSendDtls',data)
    if (data.direction === "clientSend" && this._role !== "proxy") {
      await this.initSendTransport(data.transport);
      // logger.info('sendTransport', this._sendTransport)
    }
    if (data.direction === "clientRecv") {
      await this.initRecvTransport(data.transport);
      // logger.info('recvTransport',this._recvTransport)
    }
  }
  // async initProducer(data) {
  //   // logger.info('SendTransportConnected')
  // }
  async storeProducer(data) {
    this._producerIdTemp = data.producerId;
    // logger.info('storeProducer',data)
  }
  async initConsumer(data) {
    const { producerPeerId, producerId, id, kind, rtpParameters, appData } = data;
    try {
      const consumer = await this._recvTransport.consume({
        id: id,
        producerId: producerId,
        kind: kind,
        rtpParameters: rtpParameters,
        appData: { ...appData, peerId: producerPeerId }, // Trick.
      });

      if (this._e2eKey && e2e.isSupported()) {
        e2e.setupReceiverTransform(consumer.rtpReceiver);
      }

      // Store in the map.
      this._consumers.set(consumer.id, consumer);

      consumer.on("transportclose", () => {
        this._consumers.delete(consumer.id);
      });

      const { spatialLayers, temporalLayers } = mediasoupClient.parseScalabilityMode(
        consumer.rtpParameters.encodings[0].scalabilityMode,
      );

      logger.info("spatialLayers", spatialLayers);
      logger.info("temporalLayers", temporalLayers);

      // We are ready. Set so the server will
      // resume this Consumer (which was paused for now if video).
      const payload = { consumerId: id };
      this.sendMessage("unpause_consumer", payload);

      // If audio-only mode is enabled, pause it.
      //if (consumer.kind === 'video')
      //	this._pauseConsumer(consumer);
    } catch (error) {
      logger.error("newConsumer failed", error);
      throw error;
    }
  }
  // Rename to resumeConsumer?
  async startConsumer(data) {
    const { consumerId, producerPeerId, kind, appData } = data;
    logger.info("consumer", this._consumers.get(consumerId));
    await this._consumers.get(consumerId).resume();
    const payload = {
      peerId: producerPeerId,
      consumer: this._consumers.get(consumerId),
      ready: true,
    };
    if (kind === "video") {
      if (appData.type === "screen") {
        store.commit("mediasoup/add_screen_consumer", payload);
        // logger.info('added shareconsumer')
      } else {
        store.commit("mediasoup/add_video_consumer", payload);
        // logger.info('added videoconsumer')
      }
    }

    if (kind === "audio") {
      if (appData.type === "screen") {
        store.commit("mediasoup/add_screen_audio_consumer", payload);
        // logger.info('added shareconsumer')
      } else {
        logger.info("saving audio consumer");
        store.commit("mediasoup/add_audio_consumer", payload);
        store.commit("mediasoup/audio_connected", true);
      }
    }
    logger.info("peers list", this._peers);
  }

  // Start data consumer
  async createDataConsumer(data) {
    const { producerId, id, sctpStreamParameters, label, protocol, appData } = data;
    try {
      const consumer = await this._recvTransport.consumeData({
        id: id,
        dataProducerId: producerId,
        sctpStreamParameters,
        label,
        protocol,
        appData: appData,
      });

      // Store in the map.
      this._consumers.set(consumer.id, consumer);

      consumer.on("transportclose", () => {
        this._consumers.delete(consumer.id);
      });

      consumer.on("transportclose", () => {
        this._consumers.delete(consumer.id);
      });

      consumer.on("open", () => {
        logger.info('dataConsumer "open" event');
        // Sending data channel msg when data producer state is open
        let timer = setInterval(() => {
          if (this._dataProducer?.readyState === "open") {
            logger.info("Sending data channel msg...");
            this._dataProducer.send("This is a test message");
            clearInterval(timer);
          }
        }, 500);
      });

      consumer.on("close", () => {
        logger.info('dataConsumer "close" event');

        this._consumers.delete(consumer.id);
      });

      consumer.on("error", (error) => {
        console.error('dataConsumer "error" event:%o', error);
      });

      consumer.on("message", async (message) => {
        logger.info("data channel test msg received", message);
        this._testMsgReceived = message;
        store.commit("mediasoup/set_testDataChannelOk", true);
        // Enabling mic and cam producers only when data channel test msg is received.
        if (message === "This is a test message" && this._produce && this._role !== "listener") {
          await this.enableMic();
          if (!this._externalVideo && this.webCamRole(this._role)) await this.enableWebcam();
          // logger.info('no Webcam due to external Video');
          // eslint-disable-next-line
          else this._sendTransport.on("connectionstatechange", (connectionState) => {});
        }
      });
    } catch (error) {
      console.error("create data consumer failed", error);
      throw error;
    }
  }

  async storeJoinedPeers(data) {
    // logger.info('joinedPeers', data)
    data.forEach((peer) => {
      store.commit("mediasoup/add_peer", peer);
    });

    this._gotJoinedUsers = true;
  }

  async storeAllJoinedPeers(joinedUsers) {
    let allPeers = joinedUsers;
    allPeers.push({
      id: this._id,
      displayName: this._displayName,
      device: this._device,
      role: this._role,
      produceLang: this._produceLang,
      audioConsumer: {},
      videoConsumer: {},
      onAir: this._onAir,
      micPaused: this._micPaused,
      vidPaused: this._vidPaused,
      isModerator: this._isModerator,
    });
    store.commit("mediasoup/set_allPeers", allPeers);
    // this._gotJoinedUsers = true;
  }

  async initSendTransport(transport) {
    // logger.info('transport._data',transport._data)
    const { id, iceParameters, iceCandidates, dtlsParameters, sctpParameters } = transport;

    this._sendTransport = this._mediasoupDevice.createSendTransport({
      id,
      iceParameters,
      iceCandidates,
      dtlsParameters,
      sctpParameters,
      iceServers: [],
      proprietaryConstraints: PC_PROPRIETARY_CONSTRAINTS,
      additionalSettings: {
        encodedInsertableStreams: this._e2eKey && e2e.isSupported(),
      },
    });

    // getting transport stats every 10 seconds
    // setInterval(async () =>
    // {
    // 	const stats = await this._sendTransport.getStats('outbound-rtp');
    // 	stats.forEach(stat => logger.info(JSON.stringify(stat)))
    // 	// logger.info('sending stats', stats);
    // }, 10000);

    this._sendTransport.on(
      "connect",
      (
        { dtlsParameters },
        callback,
        errback, // eslint-disable-line no-shadow
      ) => {
        logger.info("transport connected");
        const payload = {
          transportId: this._sendTransport.id,
          direction: "clientSend",
          dtlsParameters: dtlsParameters,
        };
        this.sendMessage("user_dtlsParameters", payload);
        try {
          callback();
        } catch (error) {
          errback(error);
        }
      },
    );
    this._sendTransport.on("produce", async ({ kind, rtpParameters, appData }, callback, errback) => {
      const payload = {
        transportId: id,
        routerId: this._routerId,
        kind: kind,
        rtpParameters: rtpParameters,
        appData: appData,
      };
      this.sendMessage("user_produce", payload);
      this.waitForProducerId(this._timeout).then(async () => {
        try {
          const id = this._producerIdTemp;
          this._producerIdTemp = null;
          callback({ id });
        } catch (error) {
          errback(error);
        }
      });
    });

    this._sendTransport.on(
      "producedata",
      async ({ sctpStreamParameters, label, protocol, appData }, callback, errback) => {
        const payload = {
          transportId: id,
          routerId: this._routerId,
          sctpStreamParameters: sctpStreamParameters,
          label: label,
          protocol: protocol,
          appData: appData,
        };
        this.sendMessage("user_produce_data", payload);
        this.waitForProducerId(this._timeout).then(async () => {
          try {
            const id = this._producerIdTemp;
            this._producerIdTemp = null;
            callback({ id });
          } catch (error) {
            errback(error);
          }
        });
      },
    );

    // Enabling data producer
    // await this.enableDataProducer();
    // TODO: remove
    store.commit("mediasoup/set_testDataChannelOk", true);

    if (this._produce && this._role !== "listener") {
      await this.enableMic();
      if (!this._externalVideo && this.webCamRole(this._role)) await this.enableWebcam();
      // eslint-disable-next-line
      else this._sendTransport.on("connectionstatechange", (connectionState) => {});
    }
  }

  async initRecvTransport(transport) {
    logger.info("transport", transport);
    const id = transport.id;
    const { iceParameters, iceCandidates, dtlsParameters, sctpParameters } = transport;

    this._recvTransport = this._mediasoupDevice.createRecvTransport({
      id,
      iceParameters,
      iceCandidates,
      dtlsParameters,
      sctpParameters,
      iceServers: [],
      proprietaryConstraints: PC_PROPRIETARY_CONSTRAINTS,
      additionalSettings: {
        encodedInsertableStreams: this._e2eKey && e2e.isSupported(),
      },
    });

    // getting transport stats every 10 seconds
    // setInterval(async () =>
    // {
    // 	const stats = await this._recvTransport.getStats();
    // 	logger.info('receiving stats', stats);
    // }, 10000);

    this._recvTransport.on(
      "connect",
      (
        { dtlsParameters },
        callback,
        errback, // eslint-disable-line no-shadow
      ) => {
        const payload = {
          transportId: this._recvTransport.id,
          direction: "clientRecv",
          dtlsParameters: dtlsParameters,
        };
        this.sendMessage("user_dtlsParameters", payload);

        try {
          callback();
        } catch (error) {
          errback(error);
        }
      },
    );
    if (this._consume) {
      // logger.info('readytoConsume')
      this.sendMessage("user_ready_to_consume", {
        transportId: id,
        routerId: this._routerId,
      });
    }
  }

  // Waiting for joined users
  waitForJoinedUsersCount(timeout) {
    const start = Date.now();

    return new Promise(this.waitJoinedUsersCount.bind(this, timeout, start));
  }
  waitJoinedUsersCount(timeout, start, resolve, reject) {
    logger.info("waitForJoinedUsersCount...");

    if (this._gotJoinedUsers) {
      // logger.info('got joined users: ', store.getters['mediasoup/getPeers']);
      resolve(store.getters["mediasoup/getHostsCount"]);
    } else if (timeout && Date.now() - start >= timeout) reject(new Error("timeout"));
    else setTimeout(this.waitJoinedUsersCount.bind(this, timeout, start, resolve, reject), 500);
  }
  // Create Room in FB

  //Room Observer to check if Presentation is started

  // Observe FB Transports which will be created by Server
  // Wait for Producer to get created in Server after Triggering by client
  waitForProducerId(timeout) {
    const start = Date.now();

    return new Promise(this.waitProducer.bind(this, timeout, start));
  }
  waitProducer(timeout, start, resolve, reject) {
    logger.info("waitforProducerid...");
    if (this._producerIdTemp) {
      logger.info("got Producerid: ", this._producerIdTemp);

      resolve(this._producerIdTemp);
    } else if (timeout && Date.now() - start >= timeout) reject(new Error("timeout"));
    else setTimeout(this.waitProducer.bind(this, timeout, start, resolve, reject), 500);
  }
  // Observe FB Producer and when producerId is given by Server store in temp to fulfil waitProducer

  webCamRole(role) {
    if (role === "host") return true;
    if (role === "translator") return true;

    if (role === "viewer") return true;

    if (role === "presentator") return true;
    if (role === "hybrid") return true;
  }

  async join() {
    // changing video constrained for hybrid user
    if (this._role === "hybrid") {
      VIDEO_CONSTRAINTS = {
        video: {
          width: { min: 320, ideal: 1920, max: 1920 },
          height: { min: 240, ideal: 1080, max: 1080 },
          aspectRatio: { ideal: 1.7777777778 },
        },
      };
    }

    const payload = {
      id: this._peerId,
      closed: false,
      role: this._role,
      consume: this._consume,
      produce: this._produce,
      joined: false,
      displayName: this._displayName,
      device: null,
      forceTCP: this._forceTcp,
      rtpCapabilities: null,
      sctpCapabilities: null,
      routerId: null,
      webCam: this.webCamRole(this._role),
      screenShare: false,
      audio: true,
      forceH264: this._forceH264,
      forceVP9: this._forceVP9,
      useSimulcast: this._useSimulcast,
      useSharingSimulcast: this._useSharingSimulcast,
      sendTransportId: null,
      recvTransportId: null,
      produceLang: this._produceLang,
      consumeLang: this._consumeLang,
      producers: [],
      consumers: [],
      onAir: false,
      micPaused: this._role === "translator",
      vidPaused: false,
      isModerator: this._isModerator,
      // lastAlive: Date.now(),
      connectionType: this._connectionType,
      handRaised: false,
      reqToSpeakOngoing: false,
      allowedToSpeak: this._allowedToSpeak,
      clientTranslator: this._clientTranslator,
      audioConsumer: {},
      videoConsumer: {},
    };
    this.sendMessage("user_join", { peer: payload });

    this.sendKeepAliveWithRandInterval(10000, 60000);

    if (this.isAutoplayNeeded()) await this.enableAudioAutoPlay();

    // setting own connection type to vuex store
    if (store.getters["mediasoup/browserName"] !== "firefox") {
      store.commit("mediasoup/set_connectionType", navigator.connection.effectiveType);

      let connectionTypeOld = navigator.connection.effectiveType;
      setInterval(() => {
        const connectionTypeNew = navigator.connection.effectiveType;
        if (connectionTypeOld !== connectionTypeNew) {
          // setting own connection type to vuex store
          store.commit("mediasoup/set_connectionType", connectionTypeNew);

          // sending message only if connection strength changes
          this.sendAllMessage("connection_strength", {
            connectionType: navigator.connection.effectiveType,
          });
        }
        connectionTypeOld = connectionTypeNew;
      }, 1000);
    }
  }

  async enableMic() {
    logger.info("enableMic()");

    if (this._micProducer) return;

    if (!this._mediasoupDevice.canProduce("audio")) {
      console.error("enableMic() | cannot produce audio");

      return;
    }

    let track;

    try {
      if (!this._externalVideo) {
        logger.info("enableMic() | calling getUserMedia()");

        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            deviceId: { ideal: store.getters["mediasoup/currentMicId"] },
            ...AUDIO_CONSTRAINTS,
          },
        });

        track = stream.getAudioTracks()[0];
      } else {
        const stream = await this._getExternalVideoStream();

        track = stream.getAudioTracks()[0].clone();
      }

      this._micProducer = await this._sendTransport.produce({
        track,
        codecOptions: {
          opusStereo: 1,
          opusDtx: 1,
        },
        appData: {
          type: "mic",
        },
        // NOTE: for testing codec selection.
        // codec : this._mediasoupDevice.rtpCapabilities.codecs
        // 	.find((codec) => codec.mimeType.toLowerCase() === 'audio/pcma')
      });

      if (this._e2eKey && e2e.isSupported()) {
        e2e.setupSenderTransform(this._micProducer.rtpSender);
      }

      store.commit("mediasoup/set_audio_producer", {
        id: this._micProducer.id,
        paused: this._micProducer.paused,
        track: this._micProducer.track,
        rtpParameters: this._micProducer.rtpParameters,
        codec: this._micProducer.rtpParameters.codecs[0].mimeType.split("/")[1],
        ready: true,
      });

      this._micProducer.on("transportclose", () => {
        this._micProducer = null;
      });

      this._micProducer.on("trackended", () => {
        this.disableMic().catch(() => {});
      });

      if (this._role === "translator" || store.getters["mediasoup/micPaused"] || this._role === "viewer") {
        this.muteMic();
        store.commit("mediasoup/set_testDataChannelOk", true);
      }
      //this.micObserver()
    } catch (error) {
      // logger.info('enableMic() | failed:%o', error);

      if (track) track.stop();
    }
  }

  async disableMic() {
    logger.info("disableMic()");

    if (!this._micProducer) return;

    this._micProducer.close();

    this._micProducer = null;
  }

  async muteMic() {
    this._micProducer.pause();

    try {
      this.micPaused = true;
      store.commit("mediasoup/set_mute", true);
      const payload = { peerToMute: this._peerId, micPaused: this.micPaused };
      this.sendAllMessage("peer_mute", payload);
    } catch (error) {
      logger.error("muteMic() | failed: %o", error);
    }
  }

  async unmuteMic() {
    logger.info("unmuteMic()");

    this._micProducer.resume();

    try {
      this.micPaused = false;
      store.commit("mediasoup/set_mute", false);
      const payload = { peerToMute: this._peerId, micPaused: this.micPaused };
      this.sendAllMessage("peer_mute", payload);
    } catch (error) {
      logger.error("unmuteMic() | failed: %o", error);
    }
  }
  async changeMic(id) {
    // logger.info('changeMic()', id)
    try {
      await this._micProducer.track.stop();

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          deviceId: { exact: id },
          ...AUDIO_CONSTRAINTS,
        },
      });

      const track = stream.getAudioTracks()[0];

      await this._micProducer.replaceTrack({ track });
    } catch (err) {
      // logger.info('changeMic() | failed: %o', err);
    }
  }
  async changeWebcam(id) {
    try {
      this._webcamProducer.track.stop();

      logger.info("changeWebcam() | calling getUserMedia()");

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          deviceId: { ideal: id },
          ...VIDEO_CONSTRAINTS.video,
        },
      });

      const track = stream.getVideoTracks()[0];

      await this._webcamProducer.replaceTrack({ track });
      store.commit("mediasoup/set_video_producer_track", track);
      store.commit("mediasoup/changedWebcamTrack", true);
    } catch (err) {
      logger.error("changeWebcam() | failed: %o", err);
    }
  }

  async videoMute() {
    logger.info("muteVid()");

    this._webcamProducer.pause();

    try {
      this.vidPaused = true;
      store.commit("mediasoup/set_video_mute", true);
      const payload = { peerToMute: this._peerId, vidPaused: this.vidPaused };
      this.sendAllMessage("peer_video_mute", payload);
    } catch (error) {
      logger.error("muteVid() | failed: %o", error);
    }
  }
  async videoUnMute() {
    logger.info("unmuteMic()");

    this._webcamProducer.resume();

    try {
      this.vidPaused = false;
      store.commit("mediasoup/set_video_mute", false);
      const payload = { peerToMute: this._peerId, vidPaused: this.vidPaused };
      this.sendAllMessage("peer_video_mute", payload);
      store.commit("mediasoup/set_videoReady", true);
    } catch (error) {
      logger.error("unmuteVid() | failed: %o", error);
    }
  }

  async enableWebcam() {
    logger.info("enableWebcam()");

    if (this._webcamProducer) {
      // logger.info('after dis and denable im here ')
      return;
    } else if (this._shareProducer) {
      await this.disableShare();
    }

    if (!this._mediasoupDevice.canProduce("video")) {
      logger.error("enableWebcam() | cannot produce video");

      return;
    }

    let track;
    let device;

    try {
      if (!this._externalVideo) {
        await this._updateWebcams();

        device = this._webcam.device;

        // const { resolution } = this._webcam;

        if (!device) throw new Error("no webcam devices");

        logger.info("enableWebcam() | calling getUserMedia()");

        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            deviceId: { ideal: store.getters["mediasoup/currentCamId"] },
            ...VIDEO_CONSTRAINTS.video,
          },
        });
        track = stream.getVideoTracks()[0];
        logger.info("Resolution: ", track.getSettings().width, track.getSettings().height);
      } else {
        device = { label: "external video" };

        const stream = await this._getExternalVideoStream();

        track = stream.getVideoTracks()[0].clone();
      }

      let encodings;
      let codec;
      const codecOptions = {
        videoGoogleStartBitrate: 1000,
      };

      if (this._forceH264) {
        codec = this._mediasoupDevice.rtpCapabilities.codecs.find((c) => c.mimeType.toLowerCase() === "video/h264");

        if (!codec) {
          throw new Error("desired H264 codec+configuration is not supported");
        }
      } else if (this._forceVP9) {
        codec = this._mediasoupDevice.rtpCapabilities.codecs.find((c) => c.mimeType.toLowerCase() === "video/vp9");

        if (!codec) {
          throw new Error("desired VP9 codec+configuration is not supported");
        }
      }

      if (this._useSimulcast) {
        // If VP9 is the only available video codec then use SVC.
        const firstVideoCodec = this._mediasoupDevice.rtpCapabilities.codecs.find((c) => c.kind === "video");

        if ((this._forceVP9 && codec) || firstVideoCodec.mimeType.toLowerCase() === "video/vp9") {
          encodings = WEBCAM_KSVC_ENCODINGS;
        } else {
          encodings = WEBCAM_SIMULCAST_ENCODINGS;
        }
      }

      this._webcamProducer = await this._sendTransport.produce({
        track,
        encodings,
        codecOptions,
        codec,
        appData: {
          type: "webcam",
        },
      });

      if (this._e2eKey && e2e.isSupported()) {
        e2e.setupSenderTransform(this._webcamProducer.rtpSender);
      }
      store.commit("mediasoup/set_video_producer", {
        id: this._webcamProducer.id,
        deviceLabel: device.label,
        type: this._getWebcamType(device),
        paused: this._webcamProducer.paused,
        track: this._webcamProducer.track,
        rtpParameters: this._webcamProducer.rtpParameters,
        codec: this._webcamProducer.rtpParameters.codecs[0].mimeType.split("/")[1],
      });
      store.commit("mediasoup/set_videoReady", true);

      this._webcamProducer.on("transportclose", () => {
        this._webcamProducer = null;
      });

      this._webcamProducer.on("trackended", () => {
        this.disableWebcam().catch(() => {});
      });
      if (this._role === "translator" || this._role === "viewer" || store.getters["mediasoup/vidPaused"]) {
        this.videoMute();
      }
    } catch (error) {
      logger.error("enableWebcam() | failed:%o", error);

      if (track) track.stop();
    }
  }

  async disableWebcam() {
    logger.info("disableWebcam()");

    if (!this._webcamProducer) return;

    this._webcamProducer.close();

    this._webcamProducerRef = null;

    store.commit("mediasoup/set_video_producer", {
      id: null,
      deviceLabel: null,
      type: null,
      paused: null,
      track: null,
      rtpParameters: null,
      codec: null,
      ready: false,
    });

    this._webcamProducer = null;
  }

  // async changeWebcam()
  // {
  // 	logger.info('changeWebcam()');

  // 	try
  // 	{
  // 		await this._updateWebcams();

  // 		const array = Array.from(this._webcams.keys());
  // 		const len = array.length;
  // 		const deviceId =
  // 			this._webcam.device ? this._webcam.device.deviceId : undefined;
  // 		let idx = array.indexOf(deviceId);

  // 		if (idx < len - 1)
  // 			idx++;
  // 		else
  // 			idx = 0;

  // 		this._webcam.device = this._webcams.get(array[idx]);

  // 		logger.info(
  // 			'changeWebcam() | new selected webcam [device:%o]',
  // 			this._webcam.device);

  // 		// Reset video resolution to HD.
  // 		this._webcam.resolution = 'hd';

  // 		if (!this._webcam.device)
  // 			throw new Error('no webcam devices');

  // 		// Closing the current video track before asking for a new one (mobiles do not like
  // 		// having both front/back cameras open at the same time).
  // 		this._webcamProducer.track.stop();

  // 		logger.info('changeWebcam() | calling getUserMedia()');

  // 		const stream = await navigator.mediaDevices.getUserMedia(
  // 			{
  // 				video :
  // 				{
  // 					deviceId : { ideal: store.getters['mediasoup/currentCamId'] },
  // 					...VIDEO_CONSTRAINTS[this._webcam.resolution]
  // 				}
  // 			});

  // 		const track = stream.getVideoTracks()[0];

  // 		await this._webcamProducer.replaceTrack({ track });
  // 	}
  // 	catch (error)
  // 	{
  // 		logger.error('changeWebcam() | failed: %o', error);

  // 	}

  // }

  async changeWebcamResolution(resolution = "hd") {
    logger.info("changeWebcamResolution()");

    try {
      switch (resolution) {
        case "qvga":
          // logger.info('changing to vga')
          this._webcam.resolution = "qvga";
          break;
        case "vga":
          // logger.info('changing to hd')
          this._webcam.resolution = "vga";
          break;
        case "hd":
          // logger.info('changing to hdplus')
          this._webcam.resolution = "hd";
          break;
        default:
          this._webcam.resolution = "hd";
      }
      if (this._webcamProducer) {
        logger.info("changeWebcamResolution() | calling getUserMedia()");

        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            deviceId: { ideal: store.getters["mediasoup/currentCamId"] },
            ...VIDEO_CONSTRAINTS.video,
          },
        });

        const track = stream.getVideoTracks()[0];

        await this._webcamProducer.replaceTrack({ track });
        store.commit("mediasoup/set_video_producer_track", track);
        store.commit("mediasoup/changedWebcamTrack", true);
      }
    } catch (error) {
      logger.info("changeWebcamResolution() | failed: %o", error);
    }
  }

  async toggleWebcamResolution() {
    logger.info("changeWebcamResolution()");

    try {
      switch (this._webcam.resolution) {
        case "qvga":
          // logger.info('changing to vga')
          this._webcam.resolution = "vga";
          break;
        case "vga":
          // logger.info('changing to hd')
          this._webcam.resolution = "hd";
          break;
        case "hd":
          // logger.info('changing to hdplus')
          this._webcam.resolution = "qvga";
          break;
        default:
          this._webcam.resolution = "hd";
      }

      logger.info("changeWebcamResolution() | calling getUserMedia()");

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          deviceId: { ideal: store.getters["mediasoup/currentCamId"] },
          ...VIDEO_CONSTRAINTS.video,
        },
      });

      const track = stream.getVideoTracks()[0];

      await this._webcamProducer.replaceTrack({ track });
      store.commit("mediasoup/set_video_producer_track", track);
      store.commit("mediasoup/changedWebcamTrack", true);
    } catch (error) {
      logger.error("changeWebcamResolution() | failed: %o", error);
    }
  }

  async enableShare() {
    logger.info("enableShare()");

    if (this._shareProducer) return;
    else if (this._webcamProducer) {
      //await this.videoMute();
    }

    if (!this._mediasoupDevice.canProduce("video")) {
      logger.error("enableShare() | cannot produce video");

      return;
    }

    let track = null;
    let audioTrack = null;

    try {
      // logger.info('enableShare() | calling getUserMedia()');

      const stream = await navigator.mediaDevices.getDisplayMedia({
        audio: true,
        video: {
          displaySurface: "monitor",
          logicalSurface: true,
          cursor: true,
          width: { max: 1920 },
          height: { max: 1080 },
          frameRate: { max: 30 },
        },
      });

      // May mean cancelled (in some implementations).
      // logger.info('Ill not getprinted in Error case')
      if (!stream) {
        // logger.info('no stream so cancelled?')
        return;
      }

      track = stream.getVideoTracks()[0];
      audioTrack = stream.getAudioTracks()[0];

      // Creating producer for screen share audio
      if (audioTrack) {
        this._shareAudioProducer = await this._sendTransport.produce({
          track: audioTrack,
          codecOptions: {
            opusStereo: 1,
            opusDtx: 1,
          },
          appData: {
            type: "screen",
          },
        });
      } else {
        logger.info("audio track not ready");
      }

      let encodings;
      let codec;
      const codecOptions = {
        videoGoogleStartBitrate: 1000,
      };

      if (this._forceH264) {
        codec = this._mediasoupDevice.rtpCapabilities.codecs.find((c) => c.mimeType.toLowerCase() === "video/h264");

        if (!codec) {
          throw new Error("desired H264 codec+configuration is not supported");
        }
      } else if (this._forceVP9) {
        codec = this._mediasoupDevice.rtpCapabilities.codecs.find((c) => c.mimeType.toLowerCase() === "video/vp9");

        if (!codec) {
          throw new Error("desired VP9 codec+configuration is not supported");
        }
      }

      if (this._useSharingSimulcast) {
        // If VP9 is the only available video codec then use SVC.
        const firstVideoCodec = this._mediasoupDevice.rtpCapabilities.codecs.find((c) => c.kind === "video");

        if ((this._forceVP9 && codec) || firstVideoCodec.mimeType.toLowerCase() === "video/vp9") {
          encodings = SCREEN_SHARING_SVC_ENCODINGS;
        } else {
          encodings = SCREEN_SHARING_SIMULCAST_ENCODINGS.map((encoding) => ({
            ...encoding,
            dtx: true,
          }));
        }
      }

      this._shareProducer = await this._sendTransport.produce({
        track,
        encodings,
        codecOptions,
        codec,
        appData: {
          share: true,
          type: "screen",
        },
      });

      store.commit("mediasoup/set_screen_producer", {
        id: this._shareProducer.id,
        deviceLabel: "screen",
        type: "screen",
        paused: this._shareProducer.paused,
        track: this._shareProducer.track,
        rtpParameters: this._shareProducer.rtpParameters,
        codec: this._shareProducer.rtpParameters.codecs[0].mimeType.split("/")[1],
        ready: true,
      });

      if (this._e2eKey && e2e.isSupported()) {
        e2e.setupSenderTransform(this._shareProducer.rtpSender);
      }

      this._shareProducer.on("transportclose", () => {
        this._shareProducer = null;
      });

      this._shareProducer.on("trackended", () => {
        this.disableShare().catch(() => {});
        store.commit("mediasoup/set_sharing", false);
        this.stopPresentation();
      });
      this.startPresentation();
      store.commit("mediasoup/set_sharing", true);
    } catch (error) {
      // logger.info('enableShare() | failed:%o', error);

      if (error.name !== "NotAllowedError") {
        throw error;
      }

      if (track) track.stop();
      if (audioTrack) audioTrack.stop();
    }

    ////////////////////////////////////
    //// Code for screen share audio ///
    // try {
    //   const audioStream = await navigator.mediaDevices.getDisplayMedia({
    //     audio: true,
    //     video: true,
    //   });

    //   const audioTrack = this.audioStream.getAudioTracks();

    //   logger.info("screen audio track", audioTrack);
    // } catch (error) {
    //   console.error("screen audio error: ", error);
    // }
  }

  // Creates data channels on the send transport and produces data.
  async enableDataProducer() {
    logger.info("enableDataProducer()");
    try {
      this._dataProducer = await this._sendTransport.produceData({
        ordered: false,
        maxRetransmits: 1,
        label: "chat",
        priority: "medium",
        appData: { info: "my-chat-DataProducer" },
      });

      // // Sending message over datachannel
      // const testMsg = "This is a test message";
      // this._dataProducer.send(testMsg);
      // logger.info("data channel msg sent");

      // Waiting for the data channel message to arrive
      setTimeout(async () => {
        if (!this._testMsgReceived && this._testMsgReceived !== "This is a test message") {
          logger.info("testMsgReceived", this._testMsgReceived);

          Snackbar.open({
            message: msgHtml,
            position: "is-top",
            actionText: "Copy & Close",
            indefinite: true,
            onAction: () => {
              navigator.clipboard.writeText(msg);
            },
          });
          throw "Could not recieve data channel test msg.";
        }
      }, 20000);

      this._dataProducer.on("transportclose", () => {
        this._dataProducer = null;
      });

      this._dataProducer.on("open", () => {
        logger.info("data producer opened");
      });

      this._dataProducer.on("close", () => {
        logger.info('DataProducer "close" event');

        this._dataProducer = null;

        store.dispatch(
          // eslint-disable-next-line
          requestActions.notify({
            type: "error",
            text: "Chat DataProducer closed",
          }),
        );
      });

      this._dataProducer.on("error", (error) => {
        logger.info('DataProducer "error" event:%o', error);

        store.dispatch(
          // eslint-disable-next-line
          requestActions.notify({
            type: "error",
            text: `Chat DataProducer error: ${error}`,
          }),
        );
      });

      // this._dataProducer.on('bufferedamountlow', () => {
      //   logger.info('DataProducer "bufferedamountlow" event');
      // });
    } catch (error) {
      logger.info("data producer error", error);
      Snackbar.open({
        message: msgHtml,
        position: "is-top",
        actionText: "Copy & Close",
        indefinite: true,
        onAction: () => {
          navigator.clipboard.writeText(msg);
        },
      });
      throw error;
    }
  }

  async canIJoin(role) {
    let res = null;
    try {
      res = await this.getJoinedUsers();
    } catch (error) {
      console.error("could not connect to Mediasoup API ", error);
      Snackbar.open({
        message: msgHtml,
        position: "is-top",
        actionText: "Copy & Close",
        indefinite: true,
        onAction: () => {
          navigator.clipboard.writeText(msg);
        },
      });
      throw "Could not connect to Mediasoup API";
    }
    if (res && res.status != 200) {
      Snackbar.open({
        message: `Could not fetch joined users from Mediasoup API, server status code: ${res.status}`,
        position: "is-top",
        actionText: "Close",
        indefinite: true,
      });
      throw `Could not fetch from Mediasoup API, server status code: ${res.status}`;
    }
    // logger.info("response", res.status);
    let joinedUsers = [];
    try {
      joinedUsers = await res.json();
      logger.info("joined users", joinedUsers);
      await this.storeJoinedPeers(joinedUsers);
      await this.storeAllJoinedPeers(joinedUsers);
    } catch (error) {
      console.error("Invalid Json", error);
    }

    if (role === "host") {
      const joinedHosts = joinedUsers.filter((peer) => peer.joined && peer.role === "host");
      logger.info("joined hosts", joinedHosts);
      if (joinedHosts.length < 30) {
        return true;
      } else {
        return false;
      }
    } else if (role === "viewer") {
      const joinedViewers = joinedUsers.filter((peer) => peer.joined && peer.role === "viewer");
      logger.info("joined viewers", joinedViewers);
      if (joinedViewers.length < 150) {
        return true;
      } else {
        return false;
      }
    } else {
      return true;
    }
  }
  // Fetches already joined users list from medisoup server
  async getJoinedUsers() {
    const url = `${this._mediasoupConfig.MediasoupExpressAppUrl}joinedusers/${this._roomId}/`;
    return await fetch(url);
  }

  // Sets appropriate conference state after user closed
  setConferenceState(closedPeerId) {
    logger.info("User closed closed peerId: ", closedPeerId);
    // Removing spotlight when spotlighted user is kicked
    if (store.getters["mediasoup/getSpotlightPeerId"] === closedPeerId) {
      logger.info("User closed switching off spotlight ");
      store.commit("mediasoup/remove_spotlight");
      store.commit("mediasoup/set_spotlight_peerId", null);
    }
    // Get back to conference if screen shared user kicked
    if (store.getters["mediasoup/currentPresenter"] === closedPeerId) {
      store.commit("mediasoup/stop_presentation");
    }
  }

  async disableShare() {
    if (!this._shareProducer) return;

    this._shareProducer.close();

    store.commit("mediasoup/set_screen_producer", {
      id: null,
      deviceLabel: null,
      type: null,
      paused: null,
      track: null,
      rtpParameters: null,
      codec: null,
      ready: false,
    });

    this._shareProducer = null;
    if (this._shareAudioProducer) {
      this._shareAudioProducer.close();
      this._shareAudioProducer = null;
    }

    store.commit("mediasoup/set_sharing", false);
  }

  async enableAudioOnly() {
    logger.info("enableAudioOnly()");

    this.disableWebcam();

    for (const consumer of this._consumers.values()) {
      if (consumer.kind !== "video") continue;

      this._pauseConsumer(consumer);
    }
  }

  async disableAudioOnly() {
    logger.info("disableAudioOnly()");

    if (!this._webcamProducer && this._produce) {
      // this.enableWebcam();
    }

    for (const consumer of this._consumers.values()) {
      if (consumer.kind !== "video") continue;

      this._resumeConsumer(consumer);
    }
  }

  async muteRemotePeer(id) {
    this.sendPeerMessage("remote_mute", id, {});
  }

  async muteRemoteVideo(id) {
    this.sendPeerMessage("remote_video_mute", id, {});
  }
  async kickUser(id) {
    this.sendAllMessage("remote_close", { peerId: id });
  }

  async startPresentation() {
    this.sendAllMessage("presentation_started", { presenterId: this._peerId });
    store.commit("mediasoup/start_presentation", this._peerId);
    //store.commit('mediasoup/set_videoReady', false)

    //this.enableShare()
  }

  async stopPresentation() {
    this.sendAllMessage("presentation_stopped", { presenterId: store.getters["mediasoup/currentPresenter"] });
    store.commit("mediasoup/stop_presentation");
  }

  async setSpotlight(mediasoupId) {
    // Sets new spotlight
    if (!store.getters["mediasoup/isSpotlight"]) {
      store.commit("mediasoup/set_spotlight_peerId", mediasoupId);
      store.commit("mediasoup/set_spotlight");
    }
    // Switches spotlight
    else if (store.getters["mediasoup/getSpotlightPeerId"] !== mediasoupId) {
      store.commit("mediasoup/set_spotlight_peerId", mediasoupId);
    }
    // Removes spotlight
    else {
      store.commit("mediasoup/set_spotlight_peerId", null);
      store.commit("mediasoup/remove_spotlight");
    }
    // Sending msg to all notifying  about spotlighted user and spotlight state
    this.sendAllMessage("set_spotlight", { spotlightPeerId: store.getters["mediasoup/getSpotlightPeerId"] });
  }
  /**
   * Sends msg to all peers about produce lang change
   */
  async setProduceLang(lang) {
    this._produceLang = lang;
    this.sendAllMessage("changed_produceLang", {
      peerChanged: this._peerId,
      lang: lang,
    });
  }
  /**
   * Sends msg to all peers about consume lang change
   */
  async sendConsumeLangChngMsgToAll(newLang) {
    this._consumeLang = newLang;
    this.sendAllMessage("changed_consumeLang", {
      peerChanged: this._peerId,
      lang: newLang,
    });
  }
  /**
   * Used to set translator to on-air who accepted the handover
   * request from another translator.
   */
  async setOnAir(active) {
    const payload = { onAirId: this._peerId, onAir: active };
    this.sendAllMessage("peer_onAir", payload);
  }
  /**
   * Used to set translator to off-air who requested the handover.
   * This happens after another translator accepted the handover and
   * went on-air.
   */
  async setPartnerOffAir(id, forced) {
    this.sendPeerMessage("remote_offAir", id, { displayName: this._displayName, forced: forced });
  }
  async sendPassiveHandoverRequest(id) {
    this.sendPeerMessage("passive_handover_request", id, {
      requestType: "passive",
      requestPeerId: this._peerId,
      displayName: this._displayName,
    });
  }
  async sendActiveHandoverRequest(id) {
    this.sendPeerMessage("active_handover_request", id, {
      requestType: "active",
      requestPeerId: this._peerId,
      displayName: this._displayName,
    });
  }
  async setHandoverData(data) {
    logger.info("requestType", data.requestType);
    if (data.requestType === "passive") {
      store.commit("mediasoup/set_openPassiveHandoverNotification", true);
      store.commit("mediasoup/set_passiveRequestId", data.requestPeerId);
      store.commit("mediasoup/set_passiveReqDisplayName", data.displayName);
    } else if (data.requestType === "active") {
      logger.info("set_openHandoverNotification");
      store.commit("mediasoup/set_openActiveHandoverNotification", true);
      store.commit("mediasoup/set_activeRequestId", data.requestPeerId);
      store.commit("mediasoup/set_activeReqDisplayName", data.displayName);
    }
  }

  async switchToViewer(data) {
    this.muteMic();
    this.videoMute();
    store.commit("mediasoup/set_role", "viewer");
    store.commit("mediasoup/set_allowedToSpeak", { viewerId: data.newViewerId, allowedToSpeak: false });
    store.commit("mediasoup/set_reqToSpeakOngoing", { requesterId: data.newViewerId, reqToSpeakOngoing: false });
  }

  async restartIce() {
    logger.info("restartIce()");

    try {
      if (this._sendTransport) {
        // logger.info('Needstobeimplementesd')
      }

      if (this._recvTransport) {
        // logger.info('Needstobeimplementesd')
      }
    } catch (error) {
      logger.error("restartIce() | failed:%o", error);
    }
  }

  async setMaxSendingSpatialLayer(spatialLayer) {
    try {
      if (this._webcamProducer) {
        await this._webcamProducer.setMaxSpatialLayer(spatialLayer);
      } else if (this._shareProducer) {
        await this._shareProducer.setMaxSpatialLayer(spatialLayer);
      }
      logger.info("Current spatial layer: ", spatialLayer);
    } catch (error) {
      logger.info("Error setting spatial layer: ", error);
    }
  }

  async toggleMaxlayers() {
    switch (this._spatialLayers) {
      case 0:
        this._spatialLayers = 1;
        await this.setMaxSendingSpatialLayer(this._spatialLayers);
        break;
      case 1:
        this._spatialLayers = 2;
        await this.setMaxSendingSpatialLayer(this._spatialLayers);
        break;
      case 2:
        this._spatialLayers = 3;
        await this.setMaxSendingSpatialLayer(this._spatialLayers);
        break;
      case 3:
        this._spatialLayers = 0;
        await this.setMaxSendingSpatialLayer(this._spatialLayers);
        break;
    }
  }

  async setConsumerPreferredLayers(consumerId, spatialLayer, temporalLayer) {
    logger.info(
      "setConsumerPreferredLayers() [consumerId:%s, spatialLayer:%s, temporalLayer:%s]",
      consumerId,
      spatialLayer,
      temporalLayer,
    );

    try {
      // logger.info('Needstobeimplementesd')
    } catch (error) {
      logger.error("setConsumerPreferredLayers() | failed:%o", error);
    }
  }

  async setConsumerPriority(consumerId, priority) {
    logger.info("setConsumerPriority() [consumerId:%s, priority:%d]", consumerId, priority);

    try {
      // logger.info('Needstobeimplementesd')
    } catch (error) {
      logger.error("setConsumerPriority() | failed:%o", error);
    }
  }

  async requestConsumerKeyFrame(consumerId) {
    logger.info("requestConsumerKeyFrame() [consumerId:%s]", consumerId);

    try {
      // logger.info('Needstobeimplementesd')
    } catch (error) {
      logger.error("requestConsumerKeyFrame() | failed:%o", error);
    }
  }

  async changeDisplayName(displayName) {
    logger.info(`Changing display name to: `, displayName);
    this._displayName = displayName;
  }

  // TODO: impelement
  async getSendTransportRemoteStats() {
    return;
  }

  // TODO: impelement
  async getRecvTransportRemoteStats() {
    return;
  }

  // TODO: impelement
  async getAudioRemoteStats() {
    return;
  }

  // TODO: impelement
  async getVideoRemoteStats() {
    logger.info("getVideoRemoteStats()");
    // const producer = this._webcamProducer || this._shareProducer;
    return;
  }

  // TODO: impelement
  // eslint-disable-next-line
  async getConsumerRemoteStats(consumerId) {
    logger.info("getConsumerRemoteStats()");
    // const consumer = this._consumers.get(consumerId);
    return;
  }

  // TODO: impelement
  async getSendTransportLocalStats() {
    logger.info("getSendTransportLocalStats()");
    // logger.info('Needstobeimplementesd')
    return;
  }

  // TODO: impelement
  async getRecvTransportLocalStats() {
    logger.info("getRecvTransportLocalStats()");
    return;
  }

  // TODO: impelement
  async getAudioLocalStats() {
    logger.info("getAudioLocalStats()");
    return;
  }

  // TODO: impelement
  async getVideoLocalStats() {
    logger.info("getVideoLocalStats()");
    // const producer = this._webcamProducer || this._shareProducer;
    // logger.info('Needstobeimplementesd')
    return;
  }

  // TODO: impelement
  // eslint-disable-next-line
  async getConsumerLocalStats(consumerId) {
    // const consumer = this._consumers.get(consumerId);
    return;
  }

  // TODO: impelement
  getChatDataProducerRemoteStats() {
    return;
  }

  async applyNetworkThrottle(data) {
    const { uplink, downlink, rtt } = data;
    logger.info("applyNetworkThrottle", uplink, downlink, rtt);

    try {
      this.sendMessage("apply_network_throttle", data);
    } catch (error) {
      logger.error("applyNetworkThrottle() | failed:%o", error);
    }
  }

  async resetNetworkThrottle() {
    logger.info("resetNetworkThrottle");

    try {
      this.sendMessage("reset_network_throttle");
    } catch (error) {
      return;
    }
  }
  isAutoplayNeeded() {
    const browser = detect();

    if (
      (this._role !== "proxy" && !this._testViewer && this._role !== "listener") ||
      (this._role === "listener" && browser.name === "ios") ||
      (this._role === "listener" && browser.os === "iOS")
    )
      return true;

    return false;
  }
  /**
   * Enables autoplay in browser bypassing browser autoplay policy
   */
  async enableAudioAutoPlay() {
    logger.info("enableAudioAutoPlay");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });
      const audioTrack = stream.getAudioTracks()[0];

      audioTrack.enabled = false;

      setTimeout(() => audioTrack.stop(), 120000);
    } catch (error) {
      logger.warn(`Enable autoplay error: `, error);
      Toast.open({
        duration: 3000,
        message: `Mic access failed with error: ${error}`,
        position: "is-top",
        type: "is-danger",
      });
      this.close();
    }
  }

  async _updateWebcams() {
    logger.info("_updateWebcams()");

    // Reset the list.
    this._webcams = new Map();

    logger.info("_updateWebcams() | calling enumerateDevices()");

    const devices = await navigator.mediaDevices.enumerateDevices();

    for (const device of devices) {
      if (device.kind !== "videoinput") continue;

      this._webcams.set(device.deviceId, device);
    }

    const array = Array.from(this._webcams.values());
    const len = array.length;
    const currentWebcamId = this._webcam.device ? this._webcam.device.deviceId : undefined;

    logger.info("_updateWebcams() [webcams:%o]", array);

    if (len === 0) this._webcam.device = null;
    else if (!this._webcams.has(currentWebcamId)) this._webcam.device = array[0];
  }

  _getWebcamType(device) {
    if (/(back|rear)/i.test(device.label)) {
      logger.info("_getWebcamType() | it seems to be a back camera");

      return "back";
    } else {
      logger.info("_getWebcamType() | it seems to be a front camera");

      return "front";
    }
  }

  async _pauseConsumer(consumer) {
    if (consumer.paused) return;

    try {
      // logger.info('Needstobeimplementesd')
    } catch (error) {
      logger.error("_pauseConsumer() | failed:%o", error);
    }
  }

  async _resumeConsumer(consumer) {
    if (!consumer.paused) return;

    try {
      await consumer.resume();
    } catch (error) {
      logger.error("_resumeConsumer() | failed:%o", error);
    }
  }

  async _getExternalVideoStream() {
    if (this._externalVideoStream) return this._externalVideoStream;

    if (this._externalVideo.readyState < 3) {
      await new Promise((resolve) => this._externalVideo.addEventListener("canplay", resolve));
    }

    if (this._externalVideo.captureStream) this._externalVideoStream = this._externalVideo.captureStream();
    else if (this._externalVideo.mozCaptureStream) this._externalVideoStream = this._externalVideo.mozCaptureStream();
    else throw new Error("video.captureStream() not supported");

    return this._externalVideoStream;
  }

  // Generates a random within given range
  generateRandomNum(min, max) {
    return Math.floor(Math.random() * (max - min)) + min;
  }
}
